import React, { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import {
  Users,
  Clock,
  Gauge,
  TriangleAlert,
  Activity,
  Star,
  Loader2,
  Coffee,
  CircleDot,
} from "lucide-react";

import { cn } from "../../lib/utils";
import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { getPublicFileUrl } from "../../config";

// Mesma logica do dashboard: iniciais do nome como fallback quando o usuário
// ainda não tem foto salva (profilePicUrl === null / undefined).
const iniciaisUsuario = (nome) =>
	(nome || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((p) => p?.[0]?.toUpperCase?.() || "")
		.filter(Boolean)
		.join("") || "?";

const AvatarUsuarioPainel = ({ foto, nome, tamanho = 28 }) => {
	const [quebrou, setQuebrou] = useState(false);
	const temFoto = !!foto && !quebrou;
	const base =
		"inline-flex shrink-0 items-center justify-center rounded-full bg-muted overflow-hidden object-cover border border-border/40";
	if (temFoto) {
		return (
			<img
				alt={nome || "avatar"}
				src={getPublicFileUrl(foto)}
				className={base}
				style={{ width: tamanho, height: tamanho }}
				onError={() => setQuebrou(true)}
			/>
		);
	}
	return (
		<span
			className={base}
			style={{ width: tamanho, height: tamanho }}
			title={nome}
		>
			<span className="text-[11px] font-semibold uppercase text-muted-foreground">
				{iniciaisUsuario(nome)}
			</span>
		</span>
	);
};

// De quanto em quanto tempo os números se atualizam sozinhos.
const INTERVALO_MS = 15000;

const PRESENCA = {
	online: { rotulo: "Online", classe: "bg-emerald-500", icone: CircleDot },
	away: { rotulo: "Ausente", classe: "bg-amber-500", icone: Clock },
	break: { rotulo: "Em pausa", classe: "bg-sky-500", icone: Coffee },
	lunch: { rotulo: "Almoço", classe: "bg-sky-500", icone: Coffee },
	meeting: { rotulo: "Reunião", classe: "bg-violet-500", icone: Users },
	offline: { rotulo: "Offline", classe: "bg-slate-400", icone: CircleDot },
};

const emMinutos = (segundos) =>
	segundos >= 60
		? `${Math.floor(segundos / 60)}min ${segundos % 60}s`
		: `${segundos}s`;

// Número grande com rótulo curto. É o que se lê de relance num telão.
const Indicador = ({ rotulo, valor, ajuda, tom, icone: Icone }) => (
	<div className="rounded-xl border bg-card p-4">
		<div className="flex items-start justify-between gap-2">
			<p className="text-xs text-muted-foreground">{rotulo}</p>
			{Icone && (
				<Icone
					className={cn("h-4 w-4 shrink-0", tom || "text-muted-foreground")}
				/>
			)}
		</div>
		<p className={cn("mt-1 text-3xl font-semibold tabular-nums", tom)}>
			{valor}
		</p>
		{ajuda && <p className="mt-0.5 text-[11px] text-muted-foreground">{ajuda}</p>}
	</div>
);

const Lista = ({ titulo, itens, vazio }) => (
	<section className="rounded-xl border bg-card">
		<header className="border-b p-3 px-4">
			<h3 className="text-sm font-semibold">{titulo}</h3>
		</header>
		{itens.length === 0 ? (
			<p className="py-8 text-center text-xs text-muted-foreground">{vazio}</p>
		) : (
			<ul className="divide-y">
				{itens.map((item) => (
					<li
						key={item.nome}
						className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
					>
						<span className="min-w-0 truncate">{item.nome}</span>
						<span className="shrink-0 tabular-nums text-muted-foreground">
							{item.total}
						</span>
					</li>
				))}
			</ul>
		)}
	</section>
);

const OperationalPanel = () => {
	const navigate = useNavigate();
	const [dados, setDados] = useState(null);
	const [alertas, setAlertas] = useState([]);
	const [ranking, setRanking] = useState([]);
	const [aba, setAba] = useState("agora");
	const [carregando, setCarregando] = useState(true);

	const carregar = useCallback(async (silencioso) => {
		try {
			const [painel, sup, rank] = await Promise.all([
				api.get("/operational-panel"),
				api.get("/ai/supervisor/alerts?useAi=false"),
				api.get("/ai/supervisor/ranking?days=7"),
			]);
			setDados(painel.data);
			setAlertas(sup.data.alertas || []);
			setRanking(rank.data || []);
		} catch (err) {
			// Falha na atualização automática não deve encher a tela de avisos.
			if (!silencioso) toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();

		const timer = setInterval(() => carregar(true), INTERVALO_MS);
		return () => clearInterval(timer);
	}, [carregar]);

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	const {
		fila: filaRaw = {
			aguardando: 0,
			emAtendimento: 0,
			slaPercent: 0,
			foraDoSla: 0,
			maisAntigos: [],
		},
		tempos: temposRaw = {
			resolvidosHoje: 0,
			atendimentoMedioMinutos: 0,
			respostaMediaSegundos: 0,
		},
		equipe: equipeRaw = { usuarios: [] },
		distribuicao: distribuicaoRaw = {
			porFila: [],
			porConexao: [],
			porAtendente: [],
		},
		qualidade: qualidadeRaw = {
			notaMedia: null,
			avaliacoesHoje: 0,
		},
	} = dados || {};

	const fila = {
		...filaRaw,
		maisAntigos: filaRaw?.maisAntigos ?? [],
	};
	const tempos = { ...temposRaw };
	const equipe = {
		...equipeRaw,
		usuarios: equipeRaw?.usuarios ?? [],
	};
	const distribuicao = {
		porFila: distribuicaoRaw?.porFila ?? [],
		porConexao: distribuicaoRaw?.porConexao ?? [],
		porAtendente: distribuicaoRaw?.porAtendente ?? [],
	};
	const qualidade = { ...qualidadeRaw };

	const graves = alertas.filter((a) => a.gravidade === "alta").length;

	return (
		<MainContainer>
			<div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">
						Painel operacional
					</h1>
					<p className="text-xs text-muted-foreground">
						Atualiza sozinho a cada 15 segundos
					</p>
				</div>
				<span className="flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
					<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
					Ao vivo
				</span>
			</div>

			<PageTabs
				abas={[
					{ value: "agora", rotulo: "Agora", icone: Activity },
					{
						value: "alertas",
						rotulo: "Precisa de atenção",
						icone: TriangleAlert,
						contador: alertas.length,
					},
					{ value: "equipe", rotulo: "Equipe", icone: Users },
				]}
				atual={aba}
				onChange={setAba}
			/>

			<div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
				{/* ---------------- agora ---------------- */}
				{aba === "agora" && (
					<>
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
							<Indicador
								rotulo="Clientes esperando"
								valor={fila.aguardando}
								ajuda="Ninguém assumiu ainda"
								tom={fila.aguardando > 0 ? "text-amber-500" : undefined}
								icone={Clock}
							/>
							<Indicador
								rotulo="Em atendimento"
								valor={fila.emAtendimento}
								ajuda="Conversas em andamento"
								icone={Activity}
							/>
							<Indicador
								rotulo="SLA"
								valor={`${fila.slaPercent}%`}
								ajuda={`${fila.foraDoSla} fora do prazo`}
								tom={
									fila.slaPercent >= 80
										? "text-emerald-500"
										: fila.slaPercent >= 50
										? "text-amber-500"
										: "text-destructive"
								}
								icone={Gauge}
							/>
							<Indicador
								rotulo="Resolvidos hoje"
								valor={tempos.resolvidosHoje}
								ajuda={`Tempo médio: ${tempos.atendimentoMedioMinutos}min`}
								icone={Star}
							/>
						</div>

						<div className="grid gap-3 sm:grid-cols-2">
							<Indicador
								rotulo="Primeira resposta"
								valor={emMinutos(tempos.respostaMediaSegundos)}
								ajuda="Da abertura até o atendente responder"
								icone={Clock}
							/>
							<Indicador
								rotulo="Nota do atendimento"
								valor={qualidade.notaMedia || "—"}
								ajuda={`${qualidade.avaliacoesHoje} avaliação(ões) hoje`}
								icone={Star}
							/>
						</div>

						{/* quem espera há mais tempo */}
						<section className="rounded-xl border bg-card">
							<header className="border-b p-3 px-4">
								<h3 className="text-sm font-semibold">
									Esperando há mais tempo
								</h3>
							</header>
							{fila.maisAntigos.length === 0 ? (
								<p className="py-8 text-center text-xs text-muted-foreground">
									Ninguém na fila. Tudo em dia.
								</p>
							) : (
								<ul className="divide-y">
									{fila.maisAntigos.map((item) => (
										<li key={item.ticketId}>
											<button
												type="button"
												onClick={() =>
													navigate(`/tickets/${item.ticketId}`)
												}
												className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-muted/50"
											>
												<span className="min-w-0 truncate">
													Atendimento #{item.ticketId}
													{item.fila && (
														<span className="ml-2 text-xs text-muted-foreground">
															{item.fila}
														</span>
													)}
												</span>
												<span
													className={cn(
														"shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
														item.esperandoMinutos > 15
															? "bg-destructive/10 text-destructive"
															: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
													)}
												>
													{item.esperandoMinutos}min
												</span>
											</button>
										</li>
									))}
								</ul>
							)}
						</section>

						<div className="grid gap-3 sm:grid-cols-3">
							<Lista
								titulo="Por fila"
								itens={distribuicao.porFila}
								vazio="Nada em aberto."
							/>
							<Lista
								titulo="Por conexão"
								itens={distribuicao.porConexao}
								vazio="Nada em aberto."
							/>
							<Lista
								titulo="Por atendente"
								itens={distribuicao.porAtendente}
								vazio="Ninguém atendendo."
							/>
						</div>
					</>
				)}

				{/* ---------------- alertas ---------------- */}
				{aba === "alertas" && (
					<>
						{graves > 0 && (
							<div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
								<TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
								<div>
									<p className="text-sm font-medium text-destructive">
										{graves} situação(ões) exigem ação agora
									</p>
									<p className="mt-0.5 text-xs text-muted-foreground">
										Clientes esperando demais ou conversas paradas.
									</p>
								</div>
							</div>
						)}

						{alertas.length === 0 ? (
							<div className="rounded-xl border bg-card py-16 text-center">
								<Gauge className="mx-auto h-8 w-8 text-emerald-500/50" />
								<p className="mt-3 text-sm font-medium">Tudo sob controle</p>
								<p className="mt-1 text-xs text-muted-foreground">
									Nenhum atendimento precisa de atenção no momento.
								</p>
							</div>
						) : (
							<ul className="space-y-2">
								{alertas.map((alerta, indice) => (
									<li key={indice}>
										<button
											type="button"
											onClick={() =>
												navigate(`/tickets/${alerta.ticketId}`)
											}
											className="flex w-full items-start gap-3 rounded-xl border bg-card p-3 text-left transition-colors hover:bg-muted/40"
										>
											<span
												className={cn(
													"mt-0.5 h-2 w-2 shrink-0 rounded-full",
													alerta.gravidade === "alta"
														? "bg-destructive"
														: "bg-amber-500"
												)}
											/>
											<div className="min-w-0 flex-1">
												<p className="truncate text-sm font-medium">
													{alerta.contato}
												</p>
												<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
													{alerta.motivo}
												</p>
												{alerta.atendente && (
													<p className="mt-1 text-[11px] text-muted-foreground">
														Com {alerta.atendente}
													</p>
												)}
											</div>
											<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
												#{alerta.ticketId}
											</span>
										</button>
									</li>
								))}
							</ul>
						)}
					</>
				)}

				{/* ---------------- equipe ---------------- */}
				{aba === "equipe" && (
					<>
						<div className="grid gap-3 sm:grid-cols-3">
							<Indicador
								rotulo="Online"
								valor={equipe.online}
								ajuda="Prontos para atender"
								tom="text-emerald-500"
								icone={CircleDot}
							/>
							<Indicador
								rotulo="Em pausa"
								valor={equipe.emPausa}
								ajuda="Almoço, reunião ou intervalo"
								tom="text-sky-500"
								icone={Coffee}
							/>
							<Indicador
								rotulo="Total"
								valor={equipe.total}
								ajuda="Usuários ativos"
								icone={Users}
							/>
						</div>

						<section className="rounded-xl border bg-card">
							<header className="border-b p-3 px-4">
								<h3 className="text-sm font-semibold">Quem está onde</h3>
							</header>
							<ul className="divide-y">
								{equipe.usuarios.map((usuario) => {
									const p = PRESENCA[usuario.status] || PRESENCA.offline;
									return (
										<li
											key={usuario.id}
											className="flex items-center gap-3 px-4 py-2.5"
										>
											<AvatarUsuarioPainel
												foto={usuario.profilePicUrl}
												nome={usuario.name}
												tamanho={32}
											/>
											<span
												className={cn("h-2.5 w-2.5 shrink-0 -ml-5 mt-6 rounded-full ring-2 ring-card", p.classe)}
											/>
											<span className="min-w-0 flex-1 truncate text-sm">
												{usuario.name}
											</span>
											<span className="shrink-0 text-xs text-muted-foreground">
												{p.rotulo}
											</span>
										</li>
									);
								})}
							</ul>
						</section>

						<section className="rounded-xl border bg-card">
							<header className="border-b p-3 px-4">
								<h3 className="text-sm font-semibold">
									Desempenho nos últimos 7 dias
								</h3>
							</header>
							{ranking.length === 0 ? (
								<p className="py-8 text-center text-xs text-muted-foreground">
									Nenhum atendimento no período.
								</p>
							) : (
								<div className="overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b text-xs text-muted-foreground">
												<th className="px-4 py-2 text-left font-medium">
													Atendente
												</th>
												<th className="px-4 py-2 text-center font-medium">
													Atendidos
												</th>
												<th className="px-4 py-2 text-center font-medium">
													Resolvidos
												</th>
												<th className="px-4 py-2 text-center font-medium">
													Tempo médio
												</th>
											</tr>
										</thead>
										<tbody className="divide-y">
											{ranking.map((item) => (
												<tr key={item.userId}>
													<td className="px-4 py-2.5">
														<div className="flex items-center gap-2">
															<AvatarUsuarioPainel
																foto={item.profilePicUrl}
																nome={item.name}
																tamanho={26}
															/>
															<span className="truncate">{item.name}</span>
														</div>
													</td>
													<td className="px-4 py-2.5 text-center tabular-nums">
														{item.atendidos}
													</td>
													<td className="px-4 py-2.5 text-center tabular-nums">
														{item.resolvidos}
														<span className="ml-1 text-xs text-muted-foreground">
															({item.taxaResolucao}%)
														</span>
													</td>
													<td className="px-4 py-2.5 text-center tabular-nums">
														{item.tempoMedioMinutos}min
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</section>
					</>
				)}
			</div>
		</MainContainer>
	);
};

export default OperationalPanel;
